import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Loader2, Search, X } from 'lucide-react';
import { useMenu, useRestaurantCart, RestaurantCheckout } from '@happyspace/sdk/restaurant';

export default function ProductsPage() {
  const { categories, loading, error } = useMenu();
  const { items: cartItems, addItem, updateQuantity } = useRestaurantCart();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const totalItems = cartItems.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const allItems = categories?.flatMap(c =>
    c.items?.map(item => ({ ...item, categoryName: c.name })) ?? []
  ) ?? [];

  const filtered = allItems.filter(item => {
    const matchCat = activeCategory === 'all' || item.categoryName === activeCategory;
    const matchSearch =
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const getQty = (id: string) => (cartItems.find(i => i.id === id)?.quantity ?? 0);

  if (error) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-red-500 text-xl">Unable to load menu. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#F9F6F0]">
      <div className="bg-[#0A0F1E] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Our Menu</h1>
          <p className="text-gray-400 text-lg">Fresh, authentic, made from the heart</p>
          <div className="relative mt-8 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search dishes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0054e9]"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <Loader2 className="w-10 h-10 text-[#0054e9] animate-spin mb-4" />
            <p className="text-gray-500">Loading menu...</p>
          </div>
        ) : (
          <>
            <div className="flex gap-3 flex-wrap mb-10">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  activeCategory === 'all'
                    ? 'bg-[#0054e9] text-white shadow-lg shadow-blue-500/25'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                All Items
              </button>
              {categories?.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                    activeCategory === cat.name
                      ? 'bg-[#0054e9] text-white shadow-lg shadow-blue-500/25'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-gray-400 text-xl">No dishes found</p>
                <button
                  onClick={() => { setSearch(''); setActiveCategory('all'); }}
                  className="mt-4 text-[#0054e9] font-semibold"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {filtered.map(item => {
                    const qty = getQty(item.id);
                    return (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow border border-gray-100"
                      >
                        {item.image ? (
                          <div className="relative h-48 overflow-hidden">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                            {item.isVeg !== undefined && (
                              <span className={`absolute top-3 right-3 text-xs font-bold px-2 py-1 rounded-full ${
                                item.isVeg ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {item.isVeg ? '🌿 Veg' : '🍖 Non-Veg'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-[#0054e9]/10 to-[#D4AF37]/10 flex items-center justify-center">
                            <span className="text-5xl">🍛</span>
                          </div>
                        )}
                        <div className="p-5">
                          <span className="text-xs text-[#0054e9] font-semibold">{item.categoryName}</span>
                          <h3 className="font-bold text-gray-900 mt-1 mb-2">{item.name}</h3>
                          <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4">{item.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-bold text-gray-900">${item.price?.toFixed(2)}</span>
                            {qty === 0 ? (
                              <button
                                onClick={() => addItem(item)}
                                className="flex items-center gap-2 bg-[#0054e9] text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#015796] transition-colors"
                              >
                                <Plus className="w-4 h-4" /> Add
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateQuantity(item.id, qty - 1)}
                                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-6 text-center font-bold">{qty}</span>
                                <button
                                  onClick={() => updateQuantity(item.id, qty + 1)}
                                  className="w-8 h-8 bg-[#0054e9] text-white rounded-full flex items-center justify-center hover:bg-[#015796] transition-colors"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {totalItems > 0 && (
          <motion.button
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            onClick={() => setCheckoutOpen(true)}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#0054e9] text-white px-8 py-4 rounded-full font-bold shadow-2xl shadow-blue-500/40 flex items-center gap-3 hover:bg-[#015796] transition-colors z-40"
          >
            <ShoppingCart className="w-5 h-5" />
            View Cart · {totalItems} items · ${totalPrice.toFixed(2)}
          </motion.button>
        )}
      </AnimatePresence>

      <RestaurantCheckout open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </div>
  );
}
